import type { TCountry } from '@shared/types/country';
import type { TCurrency } from '@shared/types/currency';
import type { TPlacementHostCompany } from '@shared/types/placement-host-company';
import type { TImage } from '@shared/types/image';
import type { TInterviewer } from '@shared/types/interviewer';
import type { TPlacementSkill } from '@shared/types/placement-skill';
import type { TPlacementSponsor } from '@shared/types/placement-sponsor';
import type { TProgram } from '@shared/types/program';
import type { ValueOf } from '@shared/types/utilities';

export const PLACEMENT_SALARY_PER_TYPE = {
  hourly: 'HOURLY',
  daily: 'DAILY',
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
  yearly: 'YEARLY',
} as const;

export const PLACEMENT_HOUSING_RENT_TYPE = {
  daily: 'DAILY',
  weekly: 'WEEKLY',
  monthly: 'MONTHLY',
  yearly: 'YEARLY',
} as const;

export const GENDER = {
  male: 'MALE',
  female: 'FEMALE',
} as const;

export const PLACEMENT_GENDER = {
  ...GENDER,
  any: 'ANY',
} as const;

export const LANGUAGE_LEVEL = {
  beginner: 'BEGINNER',
  lowerIntermediate: 'LOWER_INTERMEDIATE',
  intermediate: 'INTERMEDIATE',
  upperIntermediate: 'UPPER_INTERMEDIATE',
  advanced: 'ADVANCED',
} as const;

export const POSITION_TYPE = {
  intern: 'INTERN',
  fullTime: 'FULL_TIME',
  partTime: 'PART_TIME',
} as const;

export const PLACEMENT_STATUS = {
  active: 'ACTIVE',
  inactive: 'INACTIVE',
  archived: 'ARCHIVED',
  draft: 'DRAFT',
  overdue: 'OVERDUE',
} as const;

export type TPlacement = {
  id: number;
  program: TProgram;
  host: TPlacementHostCompany | null;
  interviewer: TInterviewer;
  sponsor: TPlacementSponsor | null;
  required_skills: TPlacementSkill[];
  placement_images: TImage[];
  placement_files: TImage[];
  housing_images: TImage[];
  formatted_sharable_uid: number;

  created_at: string;
  updated_at: string;
  logo: string;
  status: ValueOf<typeof PLACEMENT_STATUS>;
  weather_note: string;
  community_note: string;
  cultural_opportunities_note: string;

  position_name: string;
  position_type: ValueOf<typeof POSITION_TYPE>;
  position_description: string;
  position_requirements: string;
  number_of_positions: number;

  language: string;
  language_level: ValueOf<typeof LANGUAGE_LEVEL>;

  gender: ValueOf<typeof PLACEMENT_GENDER>;
  earliest_start_date: string;
  latest_start_date: string;
  earliest_end_date: string;
  latest_end_date: string;
  application_deadline: string | null;
  avg_hours_per_week: number;

  salary_currency: TCurrency;
  salary: string;
  salary_per_type: ValueOf<typeof PLACEMENT_SALARY_PER_TYPE>;

  include_tips: boolean;
  include_utilities: boolean;

  include_housing: boolean;
  housing_name: string;
  housing_description: string;
  housing_rent_fee_currency: TCurrency;
  housing_rent_fee: string;
  housing_rent_type: ValueOf<typeof PLACEMENT_HOUSING_RENT_TYPE>;
  housing_rent_deposite_fee_currency: TCurrency;
  housing_rent_deposite_fee: string;
  housing_persons_per_room: number;
  housing_bathrooms: number;

  links: { url: string; text: string }[];

  applied_positions: number;
  approved_applications: number;
  organization: number;

  placement_address_line_1: string;
  placement_address_line_2: string;
  placement_city: string;
  placement_state: string;
  placement_country: TCountry;
  placement_postal_code: string;

  housing_address_line_1: string;
  housing_address_line_2: string;
  housing_city: string;
  housing_state: string;
  housing_country: TCountry;
  housing_postal_code: string;
};
